/** "Take Me There" for one visit (APP-1): straight to the navigation app, never through a SIFT page.
 *
 *  - A chosen app: that app's own link. A phone opens it in the app.
 *  - `device` on Android: a geo: link, so the phone opens its default navigation app or shows
 *    its own "Open with" sheet.
 *  - `device` on an iPhone: iOS has no such sheet, so a small menu of the apps on offer,
 *    remembered on this device when picked (like Salesforce's and Outlook's own menus).
 *  - `device` on a computer: Google Maps in a new tab.
 */
import React, { useState } from 'react';
import { appsFor, platformOf } from '../../maps/mapsLink';
import type { Platform } from '../../maps/mapsLink';
import { NAV_APP_LABEL } from '../../maps/navPreference';
import type { NavApp, NavPref } from '../../maps/navPreference';
import { geoStopUrl, navStopUrl } from './salesPlanFormat';
import type { PlannedStop } from '../../api/types';
import { track } from '../../telemetry/usage';

export interface TakeMeThereProps {
  stop: PlannedStop;
  pref?: NavPref;
  /** Remember an app picked from the iPhone menu. */
  onChoose?: (app: NavApp) => void;
  /** Injected in tests; read from the browser otherwise. */
  platform?: Platform;
}

const currentPlatform = (): Platform =>
  typeof navigator === 'undefined' ? 'other' : platformOf(navigator);

export const TakeMeThere: React.FC<TakeMeThereProps> = ({ stop, pref = 'device', onChoose, platform }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [remember, setRemember] = useState(true);
  const device = platform ?? currentPlatform();
  const name = stop.client_name ?? stop.client_id;
  const logTap = (app: string) =>
    track('sales_view', 'nav', 'take_me_there', { seller_uid: stop.client_id }, { app });

  // A remembered Apple Maps cannot open on Android; fall back to the phone's own choice there.
  const app: NavApp | null = pref !== 'device' && appsFor(device).includes(pref) ? pref : null;

  const direct = (target: NavApp, label = 'Take Me There', extra?: () => void) => {
    const href = navStopUrl(stop, target);
    return href ? (
      <a
        className="spc-map-link"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label === 'Take Me There' ? `Take me to ${name} in ${NAV_APP_LABEL[target]}` : undefined}
        title={`Directions in ${NAV_APP_LABEL[target]}`}
        onClick={() => { extra?.(); logTap(target); }}
      >
        {label}
      </a>
    ) : null;
  };

  if (app) return direct(app);

  if (device === 'android') {
    const href = geoStopUrl(stop);
    return href ? (
      <a
        className="spc-map-link"
        href={href}
        aria-label={`Take me to ${name}`}
        title="Directions in your navigation app"
        onClick={() => logTap('device')}
      >
        Take Me There
      </a>
    ) : null;
  }

  if (device !== 'ios') return direct('google');

  if (!navStopUrl(stop, 'google')) return null;
  return (
    <span className="spc-nav">
      <button
        type="button"
        className="spc-map-link spc-nav-toggle"
        aria-expanded={menuOpen}
        aria-label={`Take me to ${name}`}
        onClick={() => setMenuOpen((open) => !open)}
      >
        Take Me There
      </button>
      {menuOpen ? (
        <span className="spc-nav-menu" role="menu" aria-label="Navigate with">
          {appsFor(device).map((choice) => (
            <React.Fragment key={choice}>
              {direct(choice, NAV_APP_LABEL[choice], () => {
                if (remember) onChoose?.(choice);
                setMenuOpen(false);
              })}
            </React.Fragment>
          ))}
          <label className="spc-nav-remember">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            {' '}Remember on this device
          </label>
        </span>
      ) : null}
    </span>
  );
};
